import React from 'react';
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import '../styles/Admin.css';

function Admin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({
    users: 0,
    posts: 0,
    comments: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const { currentUser } = useAuth();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists() && userDoc.data().isAdmin) {
          setIsAdmin(true);
          await loadStats();
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  const loadStats = async () => {
    try {
      // Compter les utilisateurs
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersCount = usersSnapshot.size;

      // Compter les posts
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      const postsCount = postsSnapshot.size;

      // Compter les commentaires (estimation)
      let commentsCount = 0;
      const postsData = postsSnapshot.docs.map(doc => doc.data());
      postsData.forEach(post => {
        if (post.comments && Array.isArray(post.comments)) {
          commentsCount += post.comments.length;
        }
      });

      setStats({
        users: usersCount,
        posts: postsCount,
        comments: commentsCount
      });

      // Charger les utilisateurs récents
      const recentUsersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentUsersSnapshot = await getDocs(recentUsersQuery);
      const recentUsersData = recentUsersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentUsers(recentUsersData);

      // Charger les posts récents
      const recentPostsQuery = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentPostsSnapshot = await getDocs(recentPostsQuery);
      const recentPostsData = recentPostsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentPosts(recentPostsData);

    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setError('Échec de connexion. Vérifiez vos identifiants.');
      console.error('Login error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      setError('Échec de connexion avec Google.');
      console.error('Google login error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  // If not logged in, show login form
  if (!currentUser) {
    return (
      <div className="admin-container">
        <div className="admin-card">
          <h1 className="admin-title">Vision Picturale</h1>
          <h2 className="admin-subtitle">Administration</h2>

          {error && <div className="auth-error">{error}</div>}

          <form className="auth-form" onSubmit={handleEmailLogin}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <div className="admin-separator">
            <span>OU</span>
          </div>

          <button 
            onClick={handleGoogleLogin} 
            className="google-button"
            disabled={loading}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
              <g transform="matrix(1, 0, 0, 1, 0, 0)">
                <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" fill="#FFF"></path>
              </g>
            </svg>
            Continuer avec Google
          </button>
        </div>
      </div>
    );
  }

  // If logged in but not admin, show unauthorized message
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // If logged in and admin, show admin panel
  return (
    <div className="admin-container">
      <div className="admin-content">
        <h1>Tableau de bord administrateur</h1>
        <p>Bienvenue dans le panneau d'administration, {currentUser.email}</p>
        
        <div className="admin-section">
          <h2>Statistiques</h2>
          <div className="admin-stats">
            <div className="stat-card">
              <h3>Utilisateurs</h3>
              <p className="stat-number">{stats.users}</p>
            </div>
            <div className="stat-card">
              <h3>Publications</h3>
              <p className="stat-number">{stats.posts}</p>
            </div>
            <div className="stat-card">
              <h3>Commentaires</h3>
              <p className="stat-number">{stats.comments}</p>
            </div>
          </div>
        </div>
        
        <div className="admin-section">
          <h2>Utilisateurs récents</h2>
          <div className="recent-items">
            {recentUsers.map(user => (
              <div key={user.id} className="recent-item">
                <div className="item-info">
                  <h4>{user.fullName || user.username}</h4>
                  <p>{user.email}</p>
                  <small>Inscrit le {user.createdAt?.toDate?.()?.toLocaleDateString() || 'Date inconnue'}</small>
                </div>
                <div className="item-actions">
                  <button className="action-btn warning">Avertir</button>
                  <button className="action-btn danger">Suspendre</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="admin-section">
          <h2>Publications récentes</h2>
          <div className="recent-items">
            {recentPosts.map(post => (
              <div key={post.id} className="recent-item">
                <div className="item-info">
                  <h4>{post.content?.substring(0, 50)}...</h4>
                  <p>Par {post.authorName}</p>
                  <small>Publié le {post.createdAt?.toDate?.()?.toLocaleDateString() || 'Date inconnue'}</small>
                </div>
                <div className="item-actions">
                  <button className="action-btn info">Voir</button>
                  <button className="action-btn warning">Modérer</button>
                  <button className="action-btn danger">Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="admin-section">
          <h2>Actions</h2>
          <div className="admin-actions">
            <Link to="/admin/users" className="admin-action-button">
              Gérer les utilisateurs
            </Link>
            <button className="admin-action-button" onClick={() => loadStats()}>
              Actualiser les statistiques
            </button>
            <button className="admin-action-button">Exporter les données</button>
            <button className="admin-action-button">Configurer les paramètres</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
